import React from 'react';

/**
 * Card - A highly customizable card component with extensive configuration options
 * 
 * @param {Object} props
 * @param {string} props.actionType - Type of action ("No actions" or other)
 * @param {string} props.alignment - Content alignment ("Left", "Center", "Right")
 * @param {boolean} props.showLabels - Whether to show the labels section
 * @param {boolean} props.showDescription - Whether to show the description
 * @param {boolean} props.showTitle - Whether to show the title
 * @param {boolean} props.showIcon - Whether to show the icon
 * @param {string} props.swapPosition - Position of swap component ("NA", "Top", "Bottom")
 * @param {string} props.variant - Card variant ("Static" or other)
 * @param {string} props.title - Card title
 * @param {string} props.description - Card description text
 * @param {Array} props.labels - Array of label text strings
 * @param {React.ReactNode} props.icon - Icon component to display
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.showSwapComponent - Whether to show the swap component button
 * @param {string} props.swapComponentText - Text for the swap component button
 * @param {string} props.width - Card width (e.g., "360px", "100%")
 * @param {number} props.maxLabels - Maximum number of labels to display (0 = all)
 */
const Card = ({ 
  actionType = "No actions",
  alignment = "Left",
  showLabels = false,
  showDescription = true,
  showTitle = true,
  showIcon = true,
  swapPosition = "NA",
  variant = "Static",
  title = "Title",
  description = "At vero eos et accusamus et iusto odio dignissimos ducimus qui blanditiis praesentium voluptatum deleniti atque corrupti quos dolores et quas.",
  labels = ["Label", "Label"],
  icon = null,
  className = "",
  showSwapComponent = false,
  swapComponentText = "Swap component",
  width = "w-full",
  maxLabels = 2,
}) => {
  // Default icon if none provided
  const defaultIcon = (
    <div className="size-20 rounded-full bg-purple-600 flex items-center justify-center">
      <div className="size-14 rounded-full bg-yellow-300 flex items-center justify-center">
        <div className="size-10 flex flex-col items-center justify-center">
          <div className="w-8 h-4 border-b-4 border-black rounded-b-full"></div>
          <div className="w-2 h-2 bg-black rounded-full absolute top-8 left-7"></div>
          <div className="w-2 h-2 bg-black rounded-full absolute top-8 right-7"></div>
        </div>
      </div>
    </div>
  );
  
  // Filter labels based on maxLabels
  const displayLabels = maxLabels > 0 ? labels.slice(0, maxLabels) : labels;
  
  // Render labels
  const renderLabels = () => {
    if (!showLabels || displayLabels.length === 0) return null;
    
    return (
      <div className="self-stretch inline-flex justify-start items-start gap-3 flex-wrap">
        {displayLabels.map((label, index) => (
          <div key={index} data-themes="Black" data-variant="Leading Icon" className="flex justify-start items-start mb-2">
            <div data-size="Medium" data-variant="Leading icon" className="flex justify-start items-center gap-0.5">
              <div className="size-4 relative overflow-hidden">
                <div className="size-2.5 left-[2.67px] top-[2.67px] absolute bg-zinc-800"></div>
              </div>
              <div className="text-center justify-start text-zinc-800 text-sm font-['Arial'] leading-normal">
                {label}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };
  
  // Render swap component button
  const renderSwapComponent = () => {
    if (!showSwapComponent) return null;
    
    return (
      <div className="self-stretch inline-flex justify-start items-start gap-3">
        <div className="w-full p-1 bg-sky-100 rounded-sm outline outline-1 outline-offset-[-1px] outline-blue-600 flex justify-start items-center gap-2">
          <div className="size-4 relative bg-blend-multiply bg-white overflow-hidden">
            <div className="size-2.5 left-[2.98px] top-[3px] absolute bg-blue-600"></div>
          </div>
          <div className="flex-1 justify-start text-blue-600 text-xs font-['Arial'] leading-none">
            {swapComponentText}
          </div>
        </div>
      </div>
    );
  };
  
  // Determine text alignment class based on alignment prop
  const getAlignmentClass = () => {
    switch (alignment) {
      case "Center": return "items-center text-center";
      case "Right": return "items-end text-right";
      default: return "items-start text-left";
    }
  };
  
  return (
    <div 
      data-action-type={actionType} 
      data-alignement={alignment} 
      data-labels={showLabels ? "Yes" : "No"} 
      data-swap-position={swapPosition} 
      data-variant={variant} 
      style={{ width }}
      className={`p-6 rounded-xl outline outline-1 outline-offset-[-1px] outline-neutral-200 inline-flex flex-col justify-start ${getAlignmentClass()} gap-4 ${className}`}
    >
      <div className="self-stretch flex flex-col justify-start items-start gap-4">
        {/* Icon */}
        {showIcon && (
          <div data-size="XXL" data-type="Round" data-variant="Image" className="size-20 rounded-full flex flex-col justify-start items-start">
            {icon || defaultIcon}
          </div>
        )}
        
        {/* Content */}
        <div className="self-stretch flex flex-col justify-start items-start gap-2">
          {/* Swap component if position is Top */}
          {swapPosition === "Top" && showSwapComponent && renderSwapComponent()}
          
          <div className="self-stretch flex flex-col justify-start items-start gap-1">
            {/* Labels */}
            {renderLabels()}
            
            {/* Title */}
            {showTitle && (
              <div className="self-stretch justify-start text-neutral-900 text-2xl font-bold font-['Arial'] capitalize leading-9">
                {title}
              </div>
            )}
          </div>
          
          {/* Description */}
          {showDescription && (
            <div className="self-stretch justify-start text-neutral-900 text-[16px] font-normal font-['Arial'] leading-normal">
              {description}
            </div>
          )}
        </div>
      </div>
      
      {/* Bottom swap component if position is Bottom */}
      {swapPosition === "Bottom" && showSwapComponent && renderSwapComponent()}
    </div>
  );
};

export default Card;